import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { AdaptivePracticeContext } from '../../../context/context';
import { PlayFrame } from '../../adaptivePractice/frame/PlayFrame';

/**
 * A question on the play screen - the same component the child answers on - shown either at card
 * size or filling the window.
 *
 * Expanding is done HERE, by the frame this question is already in: the element keeps its place in
 * the tree and only its box changes, so the question is not rendered a second time and the teacher
 * sees the very thing they were looking at, larger. (An overlay that mounted its own frame would
 * render the same question twice and flash while it did.)
 *
 * In a card it is asked for the question alone: no menus, no close, no sound - there is no room for
 * them and the card has its own. Filling the window it gets the play screen's OWN controls back,
 * switched on through the same options a sitting uses: the menus down the sides, close, and refresh
 * for new numbers. Nothing is invented for this screen.
 *
 * Read-only throughout, at either size: nothing records an answer, opens an explanation, or marks
 * the engine's question object - which the card behind this is rendering from as well.
 *
 * HOW IT FITS A CARD. A question is drawn against the window - parts of it are sized in vw and vh -
 * and those units cannot be re-based onto a smaller box, so a question squeezed into a card simply
 * overflowed it. Instead the frame is laid out at the FULL size of the window, exactly as a child
 * would get it, and then scaled down to fit the card. Everything is there and all of it visible,
 * just smaller - the way the same question fits a phone, where the window itself is small.
 */
/**
 * A card holds a whole window, shown small. It is sized to what the list can actually show, so a
 * row of questions is in view at once without the page scrolling - a teacher choosing questions is
 * comparing them. How many share a row is the stylesheet's business.
 */
/** The tallest a card's question may be, so a list is still a list on a big screen. */
const MAX_CARD_HEIGHT = 520;
/** Below this the card has not been laid out yet - there is nothing to measure. */
const MIN_MEASURABLE = 40;

/** What will actually fit inside an element: its width less its own padding. */
const contentWidth = (element: HTMLElement): number => {
  const style = window.getComputedStyle(element);
  const padding = (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0);
  return element.clientWidth - padding;
};

export function QuestionFrame(props: {
  response: any;
  expanded?: boolean;
  onCollapse?: () => void;
  onRefresh?: () => void;
  refreshing?: boolean;
  /** Open the question's worked explanation - the teacher asked to see the answers. */
  showAnswer?: boolean;
}) {
  const { response, expanded, onRefresh, onCollapse, refreshing, showAnswer } = props;

  // The card's box, and how much of a window fits inside it.
  const stageRef = useRef<HTMLDivElement | null>(null);
  const [fit, setFit] = useState({ scale: 1, left: 0, top: 0 });

  useLayoutEffect(() => {
    const node = stageRef.current;
    if (!node) return;

    if (expanded) {
      // Filling the window: no scaling, and the size the stylesheet gives it.
      node.style.width = '';
      node.style.height = '';
      setFit({ scale: 1, left: 0, top: 0 });
      return;
    }

    const cardBody = node.parentElement;

    // What the last measurement was taken from. Writing the frame's size changes the card's HEIGHT,
    // which notifies the observer again - the loop the browser reports as "ResizeObserver loop
    // completed with undelivered notifications". Nothing is written unless one of these changed.
    let measuredFrom = '';

    const measure = () => {
      const windowWidth = window.innerWidth || 1;
      const windowHeight = window.innerHeight || 1;
      // The room INSIDE the card, padding excluded: clientWidth counts the padding, so sizing the
      // frame by it made every question a little wider than the space it had - and the card, which
      // clips what overflows it, cut the right-hand side off.
      const available = cardBody ? contentWidth(cardBody) : node.clientWidth;
      if (available < MIN_MEASURABLE) return;

      const from = `${Math.round(available)}x${windowWidth}x${windowHeight}`;
      if (from === measuredFrom) return;
      measuredFrom = from;

      // THE RULE: the box is exactly the window at this scale, and the scale is what the card's own
      // width allows. Both come from the same measurement, so the question can never be wider than
      // the box holding it - which is how the right-hand side of a question came to be cut off,
      // when the scale was worked out from one card's width and applied to another's.
      const scale = Math.min(available / windowWidth, MAX_CARD_HEIGHT / windowHeight);
      const width = `${Math.round(windowWidth * scale)}px`;
      const height = `${Math.round(windowHeight * scale)}px`;
      // Writing the same numbers again is still a mutation as far as the observer is concerned.
      if (node.style.width !== width || node.style.height !== height) {
        node.style.width = width;
        node.style.height = height;
      }
      setFit({ scale, left: 0, top: 0 });
    };

    /**
     * Measured on the NEXT FRAME, never inside the observer's own callback.
     *
     * Sizing the frame changes the card, and the card is what is being observed - so measuring
     * where the browser hands us the notification means resizing something mid-delivery, which is
     * what it complains about as "ResizeObserver loop completed with undelivered notifications".
     * The warning is harmless in itself, but the dev server puts it on screen as though it were
     * fatal. Deferring the write to the next frame takes it out of that cycle entirely, and one
     * pending pass is enough however many notifications arrive before it runs.
     */
    let pending = 0;
    const schedule = () => {
      if (pending) return;
      pending = requestAnimationFrame(() => {
        pending = 0;
        measure();
      });
    };

    measure();
    // And again once the browser has laid the card out: the first pass can run before it has a
    // width, and a question sized to nothing would stay that way.
    schedule();

    const observer = new ResizeObserver(schedule);
    if (cardBody) observer.observe(cardBody);
    window.addEventListener('resize', schedule);
    return () => {
      if (pending) cancelAnimationFrame(pending);
      observer.disconnect();
      window.removeEventListener('resize', schedule);
    };
  }, [expanded]);

  const context = useMemo(
    () =>
      ({
        notificationMessage: '',
        coins: undefined,
        streaks: undefined,
        isSafeZone: false,
        isLastQuestion: false,
        meter: { percentage: 0, level: '' },
        isLoader: Boolean(refreshing),
        isCoinAnimation: false,
        isShowStartPopup: false,
        isShowAdaptivePopup: false,
        isCloseFromClick: false,
        engineObj: response,
        options: {
          readOnly: true,
          showAnswer: Boolean(showAnswer),
          isMusicAllowed: false,
          // Filling the window already; the screen's own full-screen toggle has nothing to do.
          isToggleFullSceren: false,
          isShowExpNext: false,
          isShowSideMenus: Boolean(expanded),
          isShowClose: Boolean(expanded),
          isShowReload: Boolean(expanded && onRefresh),
        },
        loadContent: onRefresh || (() => {}),
        saveQuestionResponse: async () => {},
        getPopUpInfo: () => ({}),
        setShowAdaptivePopup: onCollapse || (() => {}),
        onclose: onCollapse || (() => {}),
      }) as any,
    [response, expanded, onRefresh, onCollapse, refreshing, showAnswer],
  );

  return (
    <div ref={stageRef} className={`aq-stage aq-question${expanded ? ' aq-stage-expanded' : ''}`}>
      <div
        className="aq-stage-window"
        style={
          expanded
            ? undefined
            : {
                transform: `translate(${fit.left}px, ${fit.top}px) scale(${fit.scale})`,
              }
        }
      >
        <AdaptivePracticeContext.Provider value={context}>
          <PlayFrame />
        </AdaptivePracticeContext.Provider>
      </div>
    </div>
  );
}

export default QuestionFrame;
